import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { SwiperSlide } from "swiper/react";
import { useTranslation } from "react-i18next";

import { messages, EMPTY_STRING, numberConstants } from "../../../constants";
import { ReactComponent as PlayIcon } from "../../../assets/icons/play.svg";
import { ReactComponent as StarIcon } from "../../../assets/icons/star-fill.svg";
import { ReactComponent as PhoneIcon } from "../../../assets/icons/capture-event.svg";
import { ReactComponent as ChevronLeftIcon } from "../../../assets/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "../../../assets/icons/chevron-right.svg";
import { isVideoUrl, truncateString } from "../../utility";

import IconButton from "../IconButton/IconButton";
import SliderWrapper from "../Slider/SliderWrapper";
import ProfileImage from "../ProfileImage/ProfileImage";

// Lazy load modal to improve initial render
const MediaPreviewModal = React.lazy(
  () => import("../Modal/MediaPreviewModal")
);

const ShutterBSelectionCard = ({
  shutterB,
  onSelect,
  isSelected,
  onViewProfileClick,
}) => {
  const { t } = useTranslation("common");

  const textRef = useRef(null);
  const [isClamped, setIsClamped] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(numberConstants.ZERO);

  // Stable gallery items with memo
  const galleryItems = useMemo(() => {
    const baseUrl = process.env.REACT_APP_S3_BASE_URL_PORTFOLIO;
    const galleries =
      shutterB?.sbportfolioGalleries[numberConstants.ZERO]?.gallery ?? [];

    return galleries.map((gallery) => ({
      ...gallery,
      url: gallery.imageUrl
        ? `${baseUrl}/${gallery.imageUrl}`
        : gallery.imageUrl,
      thumbnailUrl: gallery.imageThumbnailUrl
        ? `${baseUrl}/${gallery.imageThumbnailUrl}`
        : gallery.imageThumbnailUrl,
    }));
  }, [shutterB?.sbportfolioGalleries?.[numberConstants.ZERO]?.gallery]);

  // Clamp bio text only when bio changes
  useEffect(() => {
    const bio = shutterB?.sbportfolioGalleries?.[numberConstants.ZERO]?.bio;
    if (!bio || !textRef.current) return;

    const el = textRef.current;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
    const totalLines = Math.round(el.scrollHeight / lineHeight);

    setIsClamped(totalLines > 4);

    // eslint-disable-next-line
  }, [shutterB?.sbportfolioGalleries?.[numberConstants.ZERO]?.bio]);

  // Handlers
  const handleCardClick = useCallback(() => {
    onSelect();
  }, [onSelect]);

  const handleViewProfileClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (onViewProfileClick) {
        onViewProfileClick(shutterB.sbUserId, !!isSelected);
      }
    },
    [onViewProfileClick, shutterB.sbUserId, isSelected]
  );

  const handleImageClick = useCallback((index) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  }, []);

  return (
    <div
      className={`shutterb-selection-card ${
        isSelected ? "selected-card" : EMPTY_STRING
      }`}
    >
      <div className="row">
        {/* ShutterB Info */}
        <div className="col-12 col-lg custom-order-profile order-2 position-relative shutterb-info">
          <div className="d-flex align-items-start">
            <div className="flex-shrink-0 profile-image">
              <ProfileImage
                imageUrl={
                  shutterB?.sbportfolioGalleries?.[numberConstants.ZERO]
                    ?.profileImageUrl ?? EMPTY_STRING
                }
              />
            </div>
            <div>
              <p className="shutterb-name text-break mt-2 mb-0">
                {truncateString(
                  `${shutterB?.firstName} ${shutterB?.lastName}`,
                  25
                )}
              </p>

              <div>
                <StarIcon className="star-icon" />
                <span className="shuttberb-info ml-2">
                  {shutterB.averageReview
                    ? Number(shutterB.averageReview)?.toFixed(
                        numberConstants.ONE
                      )
                    : "0.0"}
                  /5
                </span>
              </div>

              <div className="line-height-normal">
                <PhoneIcon className="star-icon" />
                <span className="shuttberb-info text-break ml-2">
                  {shutterB.device?.isActive
                    ? shutterB.device?.deviceName
                    : EMPTY_STRING}
                </span>
              </div>
            </div>
          </div>

          {/* Bio */}
          <p className="shutterb-description my-2">
            {shutterB?.sbportfolioGalleries[numberConstants.ZERO]?.bio && (
              <>
                <span ref={textRef} className="clamped-text">
                  {shutterB?.sbportfolioGalleries[numberConstants.ZERO].bio}
                </span>
                {isClamped && (
                  <button
                    className="button-with-only-text"
                    onClick={handleViewProfileClick}
                  >
                    Read More
                  </button>
                )}
              </>
            )}
          </p>

          {/* Buttons */}
          <div className="profile-btn d-flex justify-content-center justify-content-sm-start align-items-center mt-4 w-100">
            <button
              className="button-common button-outline"
              onClick={handleViewProfileClick}
            >
              {t("view_profile")}
            </button>
            <button
              className="button-common button-fill ml-2"
              onClick={handleCardClick}
            >
              {isSelected ? messages.UNRESERVE : messages.RESERVE}
            </button>
          </div>
        </div>

        {/* Slider */}
        <div className="col-12 col-lg-auto custom-order-slider order-1 mb-2 mb-lg-0 p-0 fixed-slider-col p-1">
          <div
            className="slider-container"
            onClick={(e) => e.stopPropagation()}
          >
            {galleryItems?.length > numberConstants.ZERO && (
              <SliderWrapper
                spaceBetween={numberConstants.TWO}
                sliderHeight="360px"
                PrevIcon={ChevronLeftIcon}
                NextIcon={ChevronRightIcon}
                prevButtonClass="custom-swiper-button custom-swiper-button-prev"
                nextButtonClass="custom-swiper-button custom-swiper-button-next"
              >
                {galleryItems.map((gallery, index) => {
                  const isVideo = isVideoUrl(gallery.imageUrl);

                  const imagePath = isVideo
                    ? gallery.thumbnailUrl || gallery.url || EMPTY_STRING
                    : gallery.url || gallery.thumbnailUrl || EMPTY_STRING;

                  return (
                    <SwiperSlide key={index}>
                      <div className="video-thumbnail-wrapper h-100 w-100">
                        <img
                          className="h-100"
                          src={imagePath}
                          alt={`p-${index}`}
                          loading="lazy"
                          onClick={() => handleImageClick(index)}
                        />
                        {isVideo && (
                          <div className="play-icon-overlay">
                            <IconButton
                              icon={<PlayIcon />}
                              width="35px"
                              height="35px"
                              className="shutterb-profile-play-icon"
                              handleClick={() => handleImageClick(index)}
                            />
                          </div>
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </SliderWrapper>
            )}
          </div>
        </div>
      </div>

      {/* Media Modal */}
      <React.Suspense fallback={null}>
        <MediaPreviewModal
          isOpen={isModalOpen}
          currentIndex={currentIndex}
          mediaFiles={galleryItems}
          setCurrentIndex={setCurrentIndex}
          onClose={() => setIsModalOpen(false)}
        />
      </React.Suspense>
    </div>
  );
};

export default ShutterBSelectionCard;
